import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { chevron } from "../../components/Icons";
import { MaterialContext } from "../../context/MaterialContext";
import helpStyle from "../../styles/help.module.scss";
import settingsStyle from "../../styles/settings.module.scss";
import {
  CATALOG_STATUS_LABELS,
  countMaterialsByStatus,
  countMaterialsByVendor,
  dashboardBrandLabel,
  emptyUserStats,
  fetchAdminLogins,
  fetchUserStats,
  formatAdminLoginTime,
  formatSignInDevice,
  sortedBrandKeys,
} from "../../utilities/adminDashboard";
import { libraryPathForStatus } from "../../utilities/libraryFilter";
import { statusArray } from "../../utilities/MaterialDetails";
import {
  DEFAULT_PRICE_MAX,
  DEFAULT_PRICE_MIN,
  saveAdminSettings,
} from "../../utilities/adminSettings";

const SETTINGS_NAV = [
  { id: "admin-tools", label: "Admin tools" },
  { id: "admin-sign-ins", label: "Admin sign-ins" },
  { id: "material-tools", label: "Material tools" },
  { id: "price-filter", label: "Price filter" },
];

const ADMIN_TOOLS = [
  {
    to: "/register",
    title: "Register account",
    copy: "Create a designer or vendor login. Matlib emails them a password link.",
  },
];

const MATERIAL_TOOLS = [
  {
    to: "/material/add",
    title: "Add/Edit Materials",
    copy: "Create a new material or search for one to edit.",
  },
  {
    to: "/material/price",
    title: "Price Update",
    copy: "Download or upload a CSV to change material prices in bulk.",
  },
];

function StatCard({ value, label, to }) {
  const inner = (
    <>
      <span>{value}</span>
      <p>{label}</p>
    </>
  );

  if (to) {
    return (
      <Link className={`${helpStyle.statCard} ${settingsStyle.statLink}`} to={to}>
        {inner}
      </Link>
    );
  }

  return <div className={helpStyle.statCard}>{inner}</div>;
}

function VendorSettings() {
  const navigate = useNavigate();
  const { allMaterials, priceBounds, changePriceBounds } =
    useContext(MaterialContext);
  const [priceMin, setPriceMin] = useState(
    String(priceBounds?.min ?? DEFAULT_PRICE_MIN),
  );
  const [priceMax, setPriceMax] = useState(
    String(priceBounds?.max ?? DEFAULT_PRICE_MAX),
  );
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [userStats, setUserStats] = useState(emptyUserStats);
  const [userStatsState, setUserStatsState] = useState("loading");
  const [adminLogins, setAdminLogins] = useState([]);
  const [adminLoginsState, setAdminLoginsState] = useState("loading");

  const materialStats = useMemo(
    () => countMaterialsByVendor(allMaterials),
    [allMaterials],
  );
  const statusStats = useMemo(
    () => countMaterialsByStatus(allMaterials),
    [allMaterials],
  );
  const userCount = (value) =>
    userStatsState === "ready" ? value : "—";

  useEffect(() => {
    let cancelled = false;

    fetchUserStats()
      .then((stats) => {
        if (!cancelled) {
          setUserStats(stats);
          setUserStatsState("ready");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUserStatsState("error");
        }
      });

    fetchAdminLogins()
      .then((logins) => {
        if (!cancelled) {
          setAdminLogins(logins);
          setAdminLoginsState("ready");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAdminLoginsState("error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleClickScroll = (section) => {
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleSave = (event) => {
    event.preventDefault();
    const minValue = Number(priceMin);
    const maxValue = Number(priceMax);
    if (
      !Number.isFinite(minValue) ||
      !Number.isFinite(maxValue) ||
      minValue < 0 ||
      minValue >= maxValue
    ) {
      setError("Min must be a number 0 or greater, and less than max.");
      setMessage("");
      return;
    }

    const next = saveAdminSettings({
      priceMin: minValue,
      priceMax: maxValue,
    });
    if (typeof changePriceBounds === "function") {
      changePriceBounds(next.priceMin, next.priceMax);
    }
    setPriceMin(String(next.priceMin));
    setPriceMax(String(next.priceMax));
    setError("");
    setMessage(
      `Library price filter is now $${next.priceMin.toFixed(2)}–$${next.priceMax.toFixed(2)} on this browser.`,
    );
  };

  return (
    <div className={`content ${helpStyle.helpPage}`}>
      <div className={helpStyle.helpBackdrop} aria-hidden="true"></div>

      <div className={helpStyle.helpLayout}>
        <aside className={helpStyle.helpSidebar}>
          <div className={helpStyle.sidebarCard}>
            <p className={helpStyle.sidebarEyebrow}>VendorAdmin</p>
            <h1 className={helpStyle.sidebarTitle}>Settings</h1>
            <p className={helpStyle.sidebarCopy}>
              Site admin, material tools, and the library price filter for this
              browser.
            </p>

            <div className={helpStyle.sidebarNav}>
              {SETTINGS_NAV.map((item) => (
                <button
                  key={item.id}
                  className={helpStyle.navButton}
                  onClick={() => handleClickScroll(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <button
              className={helpStyle.backButton}
              onClick={() => navigate("/library")}
            >
              {chevron}
              Back to the Library
            </button>
          </div>
        </aside>

        <main className={helpStyle.helpBody}>
          <section
            className={`${helpStyle.heroPanel} ${settingsStyle.dashboard}`}
          >
            <div className={settingsStyle.dashboardIntro}>
              <p className={helpStyle.sectionEyebrow}>Dashboard</p>
              <h2 className={helpStyle.heroTitle}>Library snapshot</h2>
              <p className={helpStyle.heroCopy}>
                Material counts come from the catalog on this browser. Account
                counts come from the live user table.
              </p>
            </div>

            <div className={settingsStyle.dashboardGroup}>
              <p className={helpStyle.sectionEyebrow}>Materials</p>
              <div className={settingsStyle.dashboardStats}>
                <StatCard
                  value={materialStats.total}
                  label="All materials"
                  to="/library"
                />
                {sortedBrandKeys(materialStats.byVendor).map((vendor) => (
                  <StatCard
                    key={vendor}
                    value={materialStats.byVendor[vendor]}
                    label={dashboardBrandLabel(vendor)}
                  />
                ))}
              </div>
            </div>

            <div className={settingsStyle.dashboardGroup}>
              <p className={helpStyle.sectionEyebrow}>Catalog status</p>
              <div className={settingsStyle.dashboardStats}>
                {statusArray.map((status) => (
                  <StatCard
                    key={status}
                    value={statusStats[status]}
                    label={CATALOG_STATUS_LABELS[status]}
                    to={libraryPathForStatus(status)}
                  />
                ))}
              </div>
            </div>

            <div className={settingsStyle.dashboardGroup}>
              <p className={helpStyle.sectionEyebrow}>Accounts</p>
              <div className={settingsStyle.dashboardStats}>
                <StatCard
                  value={userCount(userStats.byType.designer)}
                  label="Designers"
                />
                <StatCard
                  value={userCount(userStats.byType.guest)}
                  label="Guests"
                />
                <StatCard
                  value={userCount(userStats.byType.vendor)}
                  label="Vendors"
                />
                <StatCard
                  value={userCount(userStats.byType.vendoradmin)}
                  label="Admins"
                />
              </div>
              {userStatsState === "error" ? (
                <p className={settingsStyle.dashboardNote}>
                  Account counts need the updated API on api.matlib.net.
                </p>
              ) : null}
            </div>

            {userStatsState === "ready" &&
            Object.keys(userStats.byBrand).length ? (
              <div className={settingsStyle.dashboardGroup}>
                <p className={helpStyle.sectionEyebrow}>Accounts by brand</p>
                <div className={settingsStyle.dashboardStats}>
                  {sortedBrandKeys(userStats.byBrand).map((brand) => (
                    <StatCard
                      key={brand}
                      value={userStats.byBrand[brand]}
                      label={dashboardBrandLabel(brand)}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </section>

          <section id="admin-tools" className={helpStyle.helpSection}>
            <p className={helpStyle.sectionEyebrow}>Site</p>
            <h2 className={helpStyle.sectionTitle}>Admin tools</h2>
            <div className={helpStyle.sectionDivider}></div>
            <p className={helpStyle.sectionParagraph}>
              Account and access tools. These do not change materials.
            </p>
            <div className={settingsStyle.toolList}>
              {ADMIN_TOOLS.map((tool) => (
                <Link
                  key={tool.to}
                  className={settingsStyle.toolLink}
                  to={tool.to}
                >
                  <strong>{tool.title}</strong>
                  <span>{tool.copy}</span>
                </Link>
              ))}
            </div>
          </section>

          <section id="admin-sign-ins" className={helpStyle.helpSection}>
            <p className={helpStyle.sectionEyebrow}>Access</p>
            <h2 className={helpStyle.sectionTitle}>Admin sign-ins</h2>
            <div className={helpStyle.sectionDivider}></div>
            <p className={helpStyle.sectionParagraph}>
              Each successful sign-in is kept here, with the network address
              and device.
            </p>
            {adminLoginsState === "error" ? (
              <p className={settingsStyle.dashboardNote}>
                Sign-in history needs the updated API on api.matlib.net.
              </p>
            ) : null}
            {adminLoginsState === "ready" && adminLogins.length === 0 ? (
              <p className={settingsStyle.dashboardNote}>
                No sign-ins recorded yet. The next sign-in will appear here.
              </p>
            ) : null}
            {adminLoginsState === "ready" && adminLogins.length ? (
              <ul className={settingsStyle.loginLog}>
                {adminLogins.map((item) => (
                  <li key={item.id}>
                    <strong>{item.name || item.email}</strong>
                    <span>{item.email}</span>
                    <span>{formatAdminLoginTime(item.loggedAt)}</span>
                    {formatSignInDevice(item.device) ? (
                      <span>{formatSignInDevice(item.device)}</span>
                    ) : null}
                    {item.ip ? <span>{item.ip}</span> : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </section>

          <section id="material-tools" className={helpStyle.helpSection}>
            <p className={helpStyle.sectionEyebrow}>Catalog</p>
            <h2 className={helpStyle.sectionTitle}>Material tools</h2>
            <div className={helpStyle.sectionDivider}></div>
            <p className={helpStyle.sectionParagraph}>
              Create, edit, and price materials. Same pages as the user menu.
            </p>
            <div className={settingsStyle.toolList}>
              {MATERIAL_TOOLS.map((tool) => (
                <Link
                  key={tool.to}
                  className={settingsStyle.toolLink}
                  to={tool.to}
                >
                  <strong>{tool.title}</strong>
                  <span>{tool.copy}</span>
                </Link>
              ))}
            </div>
          </section>

          <section id="price-filter" className={helpStyle.helpSection}>
            <p className={helpStyle.sectionEyebrow}>Library</p>
            <h2 className={helpStyle.sectionTitle}>Library price filter</h2>
            <div className={helpStyle.sectionDivider}></div>
            <p className={helpStyle.sectionParagraph}>
              These values stay on this browser until we store them on the API.
              They do not change material prices.
            </p>
            <form className={settingsStyle.form} onSubmit={handleSave}>
              <div className={settingsStyle.fields}>
                <div className={settingsStyle.field}>
                  <label htmlFor="price-min">Min</label>
                  <input
                    id="price-min"
                    type="number"
                    min="0"
                    step="0.1"
                    value={priceMin}
                    onChange={(event) => {
                      setPriceMin(event.target.value);
                      setError("");
                      setMessage("");
                    }}
                  />
                </div>
                <div className={settingsStyle.field}>
                  <label htmlFor="price-max">Max</label>
                  <input
                    id="price-max"
                    type="number"
                    min="0"
                    step="0.1"
                    value={priceMax}
                    onChange={(event) => {
                      setPriceMax(event.target.value);
                      setError("");
                      setMessage("");
                    }}
                  />
                </div>
              </div>
              {error ? (
                <p className={settingsStyle.error}>{error}</p>
              ) : null}
              {message ? (
                <p className={settingsStyle.success}>{message}</p>
              ) : null}
              <button
                className={`${helpStyle.backButton} ${settingsStyle.saveButton}`}
                type="submit"
              >
                Save range
              </button>
            </form>
          </section>
        </main>
      </div>
    </div>
  );
}

export default VendorSettings;
